import { format } from 'date-fns';
import { type MonthCaptionProps, useDayPicker } from 'react-day-picker';
import { OutlineChevronLeftIcon, OutlineChevronRightIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Row, Typography } from '@tempmee/react';

const ARROW_BUTTON_SIZE = 38;
const ARROW_BORDER_RADIUS = 6;

const ACTIVE_BOX_SHADOW = '0px 1px 2px rgba(0,0,0,0.15), 0px 3px 10px 1px rgba(0,0,0,0.15)';
const HOVER_BOX_SHADOW = '0px 2px 4px rgba(0,0,0,0.2), 0px 4px 14px 2px rgba(0,0,0,0.2)';

export function MonthNavigation({ calendarMonth, displayIndex }: MonthCaptionProps) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker();
  const displayMonth = calendarMonth.date;

  const isPrevDisabled = !previousMonth;
  const showPrev = displayIndex === 0;
  const showNext = displayIndex === 1;

  return (
    <Row align="center" style={{ gap: 10 }}>
      {showPrev && (
        <button
          type="button"
          data-testid="month-nav-prev"
          data-cy="month-nav-prev"
          disabled={isPrevDisabled}
          onClick={() => previousMonth && goToMonth(previousMonth)}
          style={{
            all: 'unset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: ARROW_BUTTON_SIZE,
            height: ARROW_BUTTON_SIZE,
            borderRadius: ARROW_BORDER_RADIUS,
            backgroundColor: isPrevDisabled ? colors.secondary[200] : colors.secondary[500],
            boxShadow: isPrevDisabled ? 'none' : ACTIVE_BOX_SHADOW,
            cursor: isPrevDisabled ? 'default' : 'pointer',
            pointerEvents: isPrevDisabled ? 'none' : 'auto',
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            if (!isPrevDisabled) {
              e.currentTarget.style.transform = 'scale(1.1)';
              e.currentTarget.style.backgroundColor = colors.secondary[350];
              e.currentTarget.style.boxShadow = HOVER_BOX_SHADOW;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = isPrevDisabled
              ? colors.secondary[200]
              : colors.secondary[500];
            e.currentTarget.style.boxShadow = isPrevDisabled ? 'none' : ACTIVE_BOX_SHADOW;
          }}
        >
          <OutlineChevronLeftIcon color={colors.shades[0]} />
        </button>
      )}

      <Typography
        as="span"
        data-testid="month-nav-label"
        data-cy="month-nav-label"
        bold
        size="heading_4"
        textAlign="center"
        style={{
          flex: 1,
          backgroundColor: colors.secondary[50],
          borderRadius: 6,
          padding: '4px 12px',
        }}
      >
        {format(displayMonth, 'MMMM')}
      </Typography>

      {showNext && (
        <button
          type="button"
          data-testid="month-nav-next"
          data-cy="month-nav-next"
          disabled={!nextMonth}
          onClick={() => nextMonth && goToMonth(nextMonth)}
          style={{
            all: 'unset',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: ARROW_BUTTON_SIZE,
            height: ARROW_BUTTON_SIZE,
            borderRadius: ARROW_BORDER_RADIUS,
            backgroundColor: colors.secondary[500],
            boxShadow: ACTIVE_BOX_SHADOW,
            cursor: 'pointer',
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.backgroundColor = colors.secondary[350];
            e.currentTarget.style.boxShadow = HOVER_BOX_SHADOW;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.backgroundColor = colors.secondary[500];
            e.currentTarget.style.boxShadow = ACTIVE_BOX_SHADOW;
          }}
        >
          <OutlineChevronRightIcon color={colors.shades[0]} />
        </button>
      )}
    </Row>
  );
}
